import React, { FunctionComponent, useContext } from 'react'
import { ViewStyle } from 'react-native'

import { BlurView } from 'expo-blur'
import { noop } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'
import tinyColor from 'tinycolor2'

import { NavBarProps } from '@lyrahealth-inc/shared-app-logic/src/features/header/types'

import { BackButtonV2 } from '../../atoms/backButton/BackButtonV2'
import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { LyraLogo } from '../../atoms/icons/LyraLogo'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Subhead } from '../../atoms/subhead/Subhead'
import { XButton } from '../../atoms/xButton/XButton'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { GradientHeader } from '../../organisms/gradientHeader/GradientHeader'
import { SubheadSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { FavoritedProvidersStarIconButton } from '../favoritedProvidersStarIconButton/FavoritedProvidersStarIconButton'
import { ProgressBar } from '../progressBar/ProgressBar'

type StyledViewProps = {
  isTransparent?: boolean
  backgroundColor?: string
  borderBottomColor?: string
  style?: ViewStyle
  theme: ThemeType
  isFixed?: boolean
  noBottomBorder?: boolean
}

const StyledView = styled.View<StyledViewProps>(
  ({ isTransparent, backgroundColor, theme, borderBottomColor, isFixed, noBottomBorder }) => {
    const styles = {
      backgroundColor,
      width: '100%',
    }

    if (isTransparent) {
      return {
        ...styles,
        backgroundColor: 'transparent',
        ...(isFixed && {
          position: 'fixed',
        }),
      }
    } else {
      return {
        ...styles,
        backgroundColor: backgroundColor ?? theme.colors.backgroundNavHeader,
        ...(!noBottomBorder && {
          borderBottomColor: borderBottomColor ?? theme.colors.dividerPrimary,
          borderBottomWidth: '1px',
        }),
        ...(isFixed && {
          position: 'fixed',
        }),
      }
    }
  },
)

const NavBarContent = styled.View<{
  theme: ThemeType
  showProgressBar: boolean
  isButtonVisible: boolean
  customStyling?: CSSObject
}>(({ theme, showProgressBar, isButtonVisible, customStyling = {} }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: isButtonVisible
    ? theme.breakpoints.isMobileSized
      ? `${theme.spacing['4px']} ${theme.spacing['16px']} ${theme.spacing['8px']} ${theme.spacing['16px']}`
      : `${theme.spacing['16px']} ${theme.spacing['20px']} ${theme.spacing['12px']} ${theme.spacing['20px']}`
    : theme.breakpoints.isMobileSized
    ? theme.spacing['16px']
    : `${theme.spacing['16px']} ${theme.spacing['24px']}`,
  ...(showProgressBar && {
    paddingBottom: theme.spacing['12px'],
  }),
  ...customStyling,
}))

const RightContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const CareNavigatorEntryPointContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.breakpoints.isMobileSized ? undefined : theme.spacing['16px'],
  marginRight: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
}))

const FavoritedProvidersIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.breakpoints.isMobileSized ? undefined : theme.spacing['16px'],
  marginRight: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
}))

const StyledXButton = styled(XButton)({
  alignSelf: 'center',
  top: 0,
})

const PlaceHolderView = styled.View<{ theme: ThemeType; showTitle: boolean }>(({ theme, showTitle }) => ({
  /** When there's no Back or Exit button but the Lyra Logo is showing (not the title) -
   * take up the same amount of space as the Back/Exit button so the Lyra Logo is centered correctly */
  ...(!showTitle && {
    width: theme.spacing['24px'],
    height: theme.spacing['24px'],
  }),
}))

const StyledProgressBar = styled(ProgressBar)<{ theme: ThemeType }>(({ theme }) => ({
  marginHorizontal: theme.breakpoints.isMinWidthTablet ? theme.spacing['24px'] : theme.spacing['16px'],
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['12px'],
}))

const LogoIconContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundTransparent,
  position: 'absolute',
  left: 0,
  right: 0,
  ...(IS_WEB
    ? {
        margin: 'auto',
        width: '124px',
      }
    : {
        top: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
      }),
}))

const TitleContainer = styled.View({
  position: 'absolute',
  left: 0,
  right: 0,
  alignItems: 'center',
  justifyContent: 'center',
})

export const NavBar: FunctionComponent<NavBarProps & { titleSize?: SubheadSize }> = ({
  onBack,
  onExit,
  isTransparent,
  backgroundColor,
  borderBottomColor,
  noBottomBorder = false,
  onLayout,
  showBackButton = true,
  showCloseButton = true,
  showProgressBar = false,
  style,
  title,
  titleLevel,
  titleSize = SubheadSize.SMALL,
  a11yFocusedElementOnLoad,
  shouldShowCareNavigatorHeaderEntrypoint = false,
  careNavigatorModal,
  careNavigatorEntryPoint,
  testId = 'NavBar',
  useTitle = false,
  alternateExitButtonText,
  showLogoMobileSized = false,
  customRightComponent,
  isFixed,
  progressBarProps: progressBarGivenProps,
  onLogoPress,
  favoritedProvidersEntrypointNavProps,
  hideLogo = false,
  customStyles,
  isBlurBackground,
}) => {
  const { colors, isDarkMode, componentStyles } = useTheme() as ThemeType
  const { progressBarPercentage: progressFromContext } = useContext(AppContext)
  const { isMinWidthTablet } = useMediaQuerySize()
  const navBg = tinyColor(backgroundColor || colors.backgroundNavHeader)
  const lightBgColor = navBg.getAlpha() === 0 ? !isDarkMode : navBg.isLight()
  const progressBarProps = {
    ...progressBarGivenProps,
    progress: progressBarGivenProps?.progress ?? progressFromContext,
  }

  const shouldShowFavoritedProvidersStarIcon =
    favoritedProvidersEntrypointNavProps?.shouldShowFavoritedProvidersStarIcon || false
  const isFavoritedProvidersStarIconFilled =
    favoritedProvidersEntrypointNavProps?.isFavoritedProvidersStarIconFilled || false
  const onFavoritedProvidersStarIconPressed =
    favoritedProvidersEntrypointNavProps?.onFavoritedProvidersStarIconPressed || noop
  const onHoverForIconExplanation = favoritedProvidersEntrypointNavProps?.onHoverForIconExplanation || noop
  const showTitle = (useTitle || !isMinWidthTablet) && !showLogoMobileSized

  const getLyraMobileIcon = () => {
    if (hideLogo) {
      return null
    }
    return (
      <LogoIconContainer
        testID={tID(`${testId}-lyraLogo`)}
        accessibilityRole='button'
        {...(onLogoPress && { onPress: onLogoPress })}
      >
        <LyraLogo fillColor={colors.icons.lyraLogoPrimaryIcon.fill} height={24} />
      </LogoIconContainer>
    )
  }

  const isBackButtonVisible = showBackButton && Boolean(onBack)
  const isCloseButtonVisible = showCloseButton && Boolean(onExit)
  const isButtonVisible = isBackButtonVisible || isCloseButtonVisible
  return (
    <>
      {isBlurBackground && (
        <>
          <BlurView
            intensity={70}
            tint={isDarkMode ? 'dark' : 'light'}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />
          <GradientHeader />
        </>
      )}
      <StyledView
        isTransparent={isTransparent}
        backgroundColor={backgroundColor}
        borderBottomColor={borderBottomColor}
        noBottomBorder={noBottomBorder}
        onLayout={onLayout}
        style={style}
        isFixed={isFixed}
        // When back button is visible, focus it on load; otherwise focus the navbar container
        ref={!isBackButtonVisible ? a11yFocusedElementOnLoad : undefined}
      >
        <NavBarContent
          showProgressBar={showProgressBar}
          isButtonVisible={isButtonVisible}
          customStyling={customStyles?.navBarContent}
        >
          <>
            {showBackButton && onBack ? (
              <BackButtonV2
                ref={a11yFocusedElementOnLoad}
                testID={tID(`${testId}-backButton`)}
                onPress={onBack}
                size={componentStyles.navBar.useSmallBackButton ? ButtonSize.SMALL : undefined}
              />
            ) : (
              <PlaceHolderView showTitle={showTitle} />
            )}
          </>
          {showTitle ? (
            <TitleContainer pointerEvents='none'>
              {title && <Subhead text={title} size={titleSize} level={titleLevel} testID={tID(`${testId}-subhead`)} />}
            </TitleContainer>
          ) : (
            getLyraMobileIcon()
          )}
          {(showCloseButton && onExit) || shouldShowCareNavigatorHeaderEntrypoint || customRightComponent ? (
            <RightContainer>
              {shouldShowFavoritedProvidersStarIcon && (
                <FavoritedProvidersIconContainer>
                  <FavoritedProvidersStarIconButton
                    isNavBarButton
                    onPressStarIconButton={onFavoritedProvidersStarIconPressed}
                    isFilled={isFavoritedProvidersStarIconFilled}
                    onIconHover={onHoverForIconExplanation}
                  />
                </FavoritedProvidersIconContainer>
              )}
              {shouldShowCareNavigatorHeaderEntrypoint && careNavigatorEntryPoint && (
                <CareNavigatorEntryPointContainer>
                  {React.cloneElement(careNavigatorEntryPoint, { lightBgColor: lightBgColor })}
                </CareNavigatorEntryPointContainer>
              )}
              {customRightComponent}
              {onExit && (
                <StyledXButton
                  testID={tID(`${testId}-exitButton`)}
                  onPress={onExit}
                  size={24}
                  alternateText={alternateExitButtonText}
                  accessibilityLabel={`${testId}-exitButton`}
                  importantForAccessibility='yes'
                />
              )}
            </RightContainer>
          ) : (
            <PlaceHolderView showTitle={showTitle} />
          )}
        </NavBarContent>
        {showProgressBar && Boolean(progressBarProps.progress) && <StyledProgressBar {...progressBarProps} />}
      </StyledView>
      {careNavigatorModal}
    </>
  )
}
